<template>
  <div class="card-body table mb-0">

    <!-- HEADER -->
    <PageHeader
      v-if="showHeading"
      title="Payments"
      class="poppins fw-normal text-nowrap mt-2"
    />

    <!-- PAYMENTS TABLE -->
    <GeneralTable
      :isProcessing="processing"
      :items="items"
      :fields="fields"
      :showEyeButton="true"
      :showActionButton="false"
      @showItem="openPaymentModal"
    />

    <!-- PAYMENT DETAILS MODAL -->
    <b-modal
      ref="paymentModal"
      hide-footer
      size="xl"
    >
      <template #modal-header="{ close }">
        <div class="d-flex align-items-center w-100 invoice-modal-header">

          <h5 class="mb-0 fw-semibold">
            Payment Details
          </h5>
          <div class="d-flex align-items-center ms-auto gap-3">

          <a
            v-if="selectedCharge.receipt_url"
            :href="selectedCharge.receipt_url"
            target="_blank"
            rel="noopener"
          >
            🧾 Stripe Receipt
          </a>

            <button
              type="button"
              class="btn-close custom-close"
              @click="close()"
            ></button>

          </div>
        </div>
      </template>
      <div v-if="selectedCharge" class="payment-info">

        <!-- BASIC INFO -->
        <div class="payment-column">
          <p><strong>Charge ID:</strong> {{ selectedCharge.id }}</p>
          <p><strong>Status:</strong> {{ humanStatus(selectedCharge.status) }}</p>
          <p><strong>Description:</strong> {{ selectedCharge.description || "—" }}</p>
          <p><strong>Currency:</strong> {{ selectedCharge.currency?.toUpperCase() || "—" }}</p>
          <p><strong>Invoice:</strong> {{ selectedCharge.invoice || "—" }}</p>
          <p><strong>Customer ID:</strong> {{ selectedCharge.customer || "—" }}</p>
        </div>

        <!-- AMOUNTS -->
        <div class="payment-column">
          <p><strong>Amount:</strong> {{ formatAmount(selectedCharge.amount) }}</p>
          <p><strong>Captured:</strong> {{ formatAmount(selectedCharge.amount_captured) }}</p>
          <p><strong>Refunded:</strong> {{ formatAmount(selectedCharge.amount_refunded) }}</p>
          <p><strong>Stripe Fee:</strong> {{ formatAmount(selectedCharge.balance_transaction?.fee) }}</p>
          <p><strong>Net Amount:</strong> {{ formatAmount(selectedCharge.balance_transaction?.net) }}</p>
          <p><strong>Created At:</strong> {{ formatDate(selectedCharge.created) }}</p>
        </div>

        <!-- CARD DETAILS -->
        <div class="payment-column">
          <h6>Card Details</h6>
          <p><strong>Brand:</strong> {{ card?.brand || "—" }}</p>
          <p><strong>Last 4:</strong> **** {{ card?.last4 || "—" }}</p>
          <p>
            <strong>Expiry:</strong>
            {{ card?.exp_month || "—" }}/{{ card?.exp_year || "—" }}
          </p>
          <p><strong>Funding:</strong> {{ card?.funding || "—" }}</p>
          <p><strong>Country:</strong> {{ card?.country || "—" }}</p>
          <p><strong>Network:</strong> {{ card?.network || "—" }}</p>
          <p><strong>CVC Check:</strong> {{ card?.checks?.cvc_check || "—" }}</p>
        </div>

        <div class="d-flex flex-column gap-3 justify-content-between">
          <!-- BILLING DETAILS -->
          <div class="payment-column">
            <h6>Billing Details</h6>
            <p><strong>Name:</strong> {{ selectedCharge.billing_details?.name || "—" }}</p>
            <p><strong>Email:</strong> {{ selectedCharge.billing_details?.email || "—" }}</p>
          </div>
  
          <!-- RISK / OUTCOME -->
          <div class="payment-column">
            <h6>Risk & Outcome</h6>
            <p><strong>Risk Level:</strong> {{ selectedCharge.outcome?.risk_level || "—" }}</p>
            <p><strong>Risk Score:</strong> {{ selectedCharge.outcome?.risk_score ?? "—" }}</p>
            <p><strong>Seller Message:</strong> {{ selectedCharge.outcome?.seller_message || "—" }}</p>
          </div>
        </div>

        <!-- FAILURE INFO -->
        <div
          v-if="selectedCharge.failure_message"
          class="payment-column full-width text-danger"
        >
          <h6>Failure Information</h6>
          <p>{{ selectedCharge.failure_message }}</p>
        </div>

      </div>
    </b-modal>

  </div>
</template>

<script>
import GeneralTable from "@/components/MagicTable/GeneralTable.vue";
import PageHeader from "@/components/static/page-header";

export default {
  name: "UserPayments",

  components: {
    GeneralTable,
    PageHeader
  },

  props: {
    user: {
      type: Object,
      required: true
    },
    showHeading: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      processing: false,
      items: [],
      selectedCharge: null,

      fields: [
        { key: "amount_formatted", label: "Amount" },
        { key: "currency", label: "Currency" },
        { key: "status", label: "Status" },
        { key: "card_brand", label: "Card" },
        { key: "card_last4", label: "Last 4" },
        { key: "description", label: "Description" },
        { key: "created_at", label: "Date" },
        { key: "action" }
      ]
    };
  },

  computed: {
    card() {
      return this.selectedCharge?.payment_method_details?.card || null;
    }
  },

  mounted() {
    this.fetchPayments();
  },

  methods: {
    /**
     * Fetch Stripe Charges
     */
    fetchPayments() {
      this.processing = true;

      this.$axios
        .get("payments/current-user-payments", {
          params: { user_id: this.user.id }
        })
        .then(res => {
          const charges = res?.data?.data?.charges || [];

          this.items = charges.map(charge => {
            const card = charge.payment_method_details?.card || {};

            return {
              id: charge.id,
              amount_formatted: this.formatAmount(charge.amount),
              currency: charge.currency?.toUpperCase() || "—",
              status: this.humanStatus(charge.status),
              description: this.getChargeDescription(charge),
              card_brand: card.brand || "—",
              card_last4: card.last4 || "—",
              created_at: this.formatDate(charge.created),

              // keep entire object for modal
              _raw: charge
            };
          });

          this.processing = false;
        })
        .catch(() => {
          this.processing = false;
        });
    },

    /**
     * Open details modal
     */
    openPaymentModal(chargeId) {
      const row = this.items.find(i => i.id === chargeId);
      this.selectedCharge = row?._raw || null;
      this.$refs.paymentModal.show();
    },

    /**
     * Helpers
     */
    formatAmount(amount) {
      if (typeof amount !== "number") return "—";
      return `$${(amount / 100).toFixed(2)}`;
    },

    formatDate(timestamp) {
      if (!timestamp) return "—";
      return new Date(timestamp * 1000).toLocaleString();
    },

    humanStatus(status) {
      if (!status) return "—";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    getChargeDescription(charge) {
      const amount = charge?.amount;

      if (amount === 900) {
        return "Monthly Subscription Purchased";
      }

      if (amount === 9900) {
        return "Yearly Subscription Purchased";
      }

      return charge?.description || "—";
    },
  }
};
</script>

<style scoped>
.payment-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.payment-column {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
}

.full-width {
  grid-column: span 2;
}

@media (max-width: 768px) {
  .payment-info {
    grid-template-columns: 1fr;
  }
  .full-width {
    grid-column: span 1;
  }
}
</style>
